import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  X,
  Sparkles,
  Bot,
  User as UserIcon,
  Clock,
  AlertTriangle,
  Target,
  GitBranch,
  ShieldAlert,
  Wrench,
  MessageSquare,
  History,
  Copy,
  CheckCheck,
  Trash2,
  Pencil,
} from 'lucide-react';
import {
  useFeatureRequest,
  useTransitionFeatureRequest,
  useDeleteFeatureRequest,
  useAddSpecComment,
} from '../../hooks/use-feature-requests';
import { useAuthStore } from '../../stores/auth-store';
import {
  FEATURE_REQUEST_STATUS_LABELS,
  FEATURE_REQUEST_STATUS_STYLES,
  FEATURE_REQUEST_STATUS_DOT,
  FEATURE_REQUEST_TYPE_LABELS,
  FEATURE_REQUEST_TYPE_STYLES,
  EFFORT_LABELS,
  EFFORT_STYLES,
  FEATURE_REQUEST_LIFECYCLE,
} from '../../lib/feature-request-constants';
import { formatDateTime, timeAgo } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import type { FeatureRequestStatus } from '../../types';

function SpecRow({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Target;
  label: string;
  value?: string | null;
  tone?: 'default' | 'warn';
}) {
  if (!value) return null;
  return (
    <div className="flex gap-3.5 py-4">
      <span
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          tone === 'warn' ? 'bg-error/10 text-error' : 'bg-primary/8 text-primary'
        }`}
      >
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-soft)]">
          {label}
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[color:var(--ink-base)]">
          {value}
        </p>
      </div>
    </div>
  );
}

function StatusTimeline({ status }: { status: FeatureRequestStatus }) {
  const isTerminal = status === 'REJECTED' || status === 'DUPLICATE';
  const steps = FEATURE_REQUEST_LIFECYCLE;
  const currentIndex = steps.indexOf(status);

  return (
    <div className="flex items-center">
      {steps.map((step, i) => {
        const reached = !isTerminal && i <= currentIndex;
        const isCurrent = step === status;
        return (
          <div key={step} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-[10px] font-bold transition-colors ${
                  isCurrent
                    ? 'border-primary bg-primary text-white'
                    : reached
                      ? 'border-primary/50 bg-primary/12 text-primary'
                      : 'border-[color:var(--surface-line-strong)] bg-[var(--surface-panel)] text-[color:var(--ink-soft)]'
                }`}
              >
                {reached && !isCurrent ? <Check size={13} /> : i + 1}
              </span>
              <span
                className={`whitespace-nowrap text-[10px] font-medium ${
                  isCurrent ? 'text-primary' : reached ? 'text-[color:var(--ink-base)]' : 'text-[color:var(--ink-soft)]'
                }`}
              >
                {FEATURE_REQUEST_STATUS_LABELS[step]}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`mx-1.5 mb-5 h-0.5 flex-1 rounded ${
                  !isTerminal && i < currentIndex ? 'bg-primary/50' : 'bg-[color:var(--surface-line-strong)]'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function FeatureRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  const { data, isLoading, isError, refetch } = useFeatureRequest(id);
  const transition = useTransitionFeatureRequest();
  const del = useDeleteFeatureRequest();
  const addComment = useAddSpecComment();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [specComment, setSpecComment] = useState('');

  const request = data?.data;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-4 py-24 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-error/10">
          <AlertTriangle size={26} className="text-error" />
        </span>
        <p className="text-sm font-medium text-[color:var(--ink-base)]">
          Nu am putut încărca cererea
        </p>
        <p className="max-w-sm text-xs text-[color:var(--ink-muted)]">
          A apărut o eroare la încărcare. Verifică conexiunea și încearcă din nou.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            Reîncearcă
          </Button>
          <Button onClick={() => navigate('/admin/feature-requests')}>
            <ArrowLeft size={16} /> Înapoi la listă
          </Button>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex flex-col items-center gap-4 py-24 text-center">
        <p className="text-sm text-[color:var(--ink-muted)]">Cererea nu a fost găsită.</p>
        <Button variant="outline" onClick={() => navigate('/admin/feature-requests')}>
          <ArrowLeft size={16} /> Înapoi la listă
        </Button>
      </div>
    );
  }

  const canDecide = isSuperAdmin && (request.status === 'AWAITING_APPROVAL' || request.status === 'IN_DISCUSSION');
  const canStartProgress = request.status === 'APPROVED';
  const canMarkImplemented = request.status === 'IN_PROGRESS';
  // Reopen also from AWAITING_APPROVAL / DUPLICATE so a wrong AI decision
  // (e.g. a false duplicate) can be corrected by continuing the discussion.
  const canReopen = isSuperAdmin && ['AWAITING_APPROVAL', 'APPROVED', 'REJECTED', 'IN_PROGRESS', 'DUPLICATE'].includes(request.status);
  const canContinueChat = request.status === 'IN_DISCUSSION';

  const runTransition = (action: string) => {
    setTransitionError(null);
    transition.mutate(
      { id: request.id, action },
      {
        onError: (err) => setTransitionError(err instanceof Error ? err.message : 'Operațiunea a eșuat.'),
      },
    );
  };

  const copySpec = async () => {
    const lines = [
      `TITLU: ${request.title}`,
      `TIP: ${FEATURE_REQUEST_TYPE_LABELS[request.type]}`,
      request.problem ? `PROBLEMA: ${request.problem}` : '',
      request.proposedSolution ? `SOLUTIA: ${request.proposedSolution}` : '',
      request.alternatives ? `ALTERNATIVE: ${request.alternatives}` : '',
      request.impact ? `IMPACT: ${request.impact}` : '',
      request.risks ? `RISCURI: ${request.risks}` : '',
      request.effortEstimate ? `EFORT: ${request.effortEstimate}` : '',
      request.aiRecommendation ? `RECOMANDARE AI: ${request.aiRecommendation}` : '',
      request.aiRecommendationReason ? `MOTIV: ${request.aiRecommendationReason}` : '',
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb + header */}
      <div>
        <button
          type="button"
          onClick={() => navigate('/admin/feature-requests')}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--ink-muted)] transition-colors hover:text-[color:var(--ink-strong)]"
        >
          <ArrowLeft size={14} /> Cereri de funcționalitate
        </button>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${FEATURE_REQUEST_TYPE_STYLES[request.type]}`}
              >
                {FEATURE_REQUEST_TYPE_LABELS[request.type].toUpperCase()}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${FEATURE_REQUEST_STATUS_STYLES[request.status]}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${FEATURE_REQUEST_STATUS_DOT[request.status]}`} />
                {FEATURE_REQUEST_STATUS_LABELS[request.status]}
              </span>
              {request.effortEstimate && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${EFFORT_STYLES[request.effortEstimate]}`}
                >
                  Efort {EFFORT_LABELS[request.effortEstimate]}
                </span>
              )}
            </div>
            <h1 className="mt-3 text-2xl font-bold leading-snug text-[color:var(--ink-strong)]">
              {request.title}
            </h1>
            <p className="mt-1.5 text-sm text-[color:var(--ink-muted)]">
              Propus de <strong className="font-medium text-[color:var(--ink-base)]">{request.proposer?.name}</strong>{' '}
              · {formatDateTime(request.createdAt)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {canDecide && (
              <>
                <Button onClick={() => setApproveOpen(true)} disabled={transition.isPending}>
                  <Check size={16} /> Aprobă
                </Button>
                <Button variant="danger" onClick={() => setRejectOpen(true)} disabled={transition.isPending}>
                  <X size={16} /> Respinge
                </Button>
              </>
            )}
            {canStartProgress && (
              <Button
                variant="secondary"
                onClick={() => runTransition('start_progress')}
                disabled={transition.isPending}
              >
                <Wrench size={16} /> Pune în lucru
              </Button>
            )}
            {canMarkImplemented && (
              <Button
                onClick={() => runTransition('mark_implemented')}
                disabled={transition.isPending}
              >
                <CheckCheck size={16} /> Marchează implementat
              </Button>
            )}
            {canReopen && (
              <Button
                variant="outline"
                onClick={() => runTransition('reopen_discussion')}
                disabled={transition.isPending}
              >
                <History size={16} /> Redeschide discuția
              </Button>
            )}
            {canContinueChat && (
              <Button onClick={() => navigate(`/admin/feature-requests/${request.id}/chat`)}>
                <MessageSquare size={16} /> Continuă discuția
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant="ghost" onClick={() => setDeleteOpen(true)} disabled={del.isPending}>
                <Trash2 size={16} /> Șterge
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Transition error banner */}
      {transitionError && (
        <div className="flex items-start gap-3 rounded-[1.25rem] border border-error/25 bg-error/6 px-5 py-4">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-error" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[color:var(--ink-strong)]">
              Operațiunea nu a reușit
            </p>
            <p className="mt-0.5 text-xs text-[color:var(--ink-muted)]">{transitionError}</p>
          </div>
          <button
            type="button"
            onClick={() => setTransitionError(null)}
            aria-label="Închide mesajul de eroare"
            className="rounded-full p-1 text-[color:var(--ink-muted)] transition-colors hover:bg-error/10 hover:text-error"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Terminal status banner */}
      {(request.status === 'REJECTED' || request.status === 'DUPLICATE') && (
        <div
          className={`flex items-start gap-3 rounded-[1.25rem] border px-5 py-4 ${
            request.status === 'REJECTED'
              ? 'border-error/25 bg-error/6'
              : 'border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)]'
          }`}
        >
          <AlertTriangle size={18} className={request.status === 'REJECTED' ? 'mt-0.5 text-error' : 'mt-0.5 text-[color:var(--ink-muted)]'} />
          <div>
            <p className="text-sm font-semibold text-[color:var(--ink-strong)]">
              {request.status === 'REJECTED' ? 'Cerere respinsă' : 'Marcată ca duplicat'}
            </p>
            {request.decisionReason && (
              <p className="mt-1 text-sm text-[color:var(--ink-base)]">{request.decisionReason}</p>
            )}
            {request.duplicateOf && (
              <p className="mt-1 text-xs text-[color:var(--ink-muted)]">
                Duplicat al: <strong>{request.duplicateOf.title}</strong>
              </p>
            )}
            {request.decidedBy && (
              <p className="mt-1 text-xs text-[color:var(--ink-soft)]">
                Decizie de {request.decidedBy.name}
                {request.decidedAt ? ` · ${formatDateTime(request.decidedAt)}` : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="app-shell-panel rounded-[1.5rem] px-6 py-5">
        <StatusTimeline status={request.status} />
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        {/* Spec */}
        <div className="lg:col-span-3">
          <div className="app-shell-panel overflow-hidden rounded-[1.5rem]">
            <div className="flex items-center justify-between border-b border-[color:var(--surface-line)] px-6 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Sparkles size={15} className="text-primary" />
                </span>
                <h2 className="text-sm font-semibold text-[color:var(--ink-strong)]">
                  Specificație generată de AI
                </h2>
              </div>
              <button
                type="button"
                onClick={copySpec}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--surface-line)] px-3 py-1.5 text-xs font-medium text-[color:var(--ink-muted)] transition-colors hover:border-[color:var(--surface-line-strong)] hover:text-[color:var(--ink-strong)]"
              >
                {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                {copied ? 'Copiat' : 'Copiază'}
              </button>
            </div>

            {request.problem || request.proposedSolution ? (
              <div className="divide-y divide-[color:var(--surface-line)] px-6">
                <SpecRow icon={Target} label="Problema" value={request.problem} />
                <SpecRow icon={Sparkles} label="Soluția propusă" value={request.proposedSolution} />
                <SpecRow icon={GitBranch} label="Alternative discutate" value={request.alternatives} />
                <SpecRow icon={Wrench} label="Impact" value={request.impact} />
                <SpecRow icon={ShieldAlert} label="Riscuri" value={request.risks} tone="warn" />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                <Clock size={22} className="text-[color:var(--ink-soft)]" />
                <p className="text-sm text-[color:var(--ink-muted)]">
                  Specificația va apărea aici când AI-ul finalizează interviul.
                </p>
              </div>
            )}
          </div>

          {/* AI recommendation */}
          {request.aiRecommendation && (
            <div
              className={`mt-5 rounded-[1.5rem] border px-6 py-5 ${
                request.aiRecommendation === 'APROBARE'
                  ? 'border-success/30 bg-success/6'
                  : 'border-error/25 bg-error/6'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full ${
                    request.aiRecommendation === 'APROBARE' ? 'bg-success/15' : 'bg-error/12'
                  }`}
                >
                  <Bot size={15} className={request.aiRecommendation === 'APROBARE' ? 'text-success' : 'text-error'} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[color:var(--ink-strong)]">
                    Recomandare AI: {request.aiRecommendation === 'APROBARE' ? 'Aprobare' : 'Respingere'}
                  </p>
                  <p className="text-xs text-[color:var(--ink-muted)]">
                    {request.effortEstimate ? `Estimare efort: ${EFFORT_LABELS[request.effortEstimate]}` : ''}
                  </p>
                </div>
              </div>
              {request.aiRecommendationReason && (
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[color:var(--ink-base)]">
                  {request.aiRecommendationReason}
                </p>
              )}
            </div>
          )}

          {/* Spec comments — reviewer notes on the AI-generated spec */}
          <div className="app-shell-panel mt-5 overflow-hidden rounded-[1.5rem]">
            <div className="flex items-center gap-2.5 border-b border-[color:var(--surface-line)] px-6 py-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/14">
                <Pencil size={15} className="text-accent-dark" />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-[color:var(--ink-strong)]">
                  Comentarii pe specificare
                </h2>
                <p className="mt-0.5 text-xs text-[color:var(--ink-muted)]">
                  Adăugări sau modificări ale specificării generate de AI, înainte de decizie.
                </p>
              </div>
            </div>

            <div className="px-6 py-4">
              {(request.specComments ?? []).length === 0 ? (
                <p className="py-2 text-center text-xs text-[color:var(--ink-muted)]">
                  Nicio comentariu încă.
                </p>
              ) : (
                <div className="space-y-3">
                  {(request.specComments ?? []).map((c) => (
                    <div
                      key={c.id}
                      className="rounded-xl border border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] px-4 py-3"
                    >
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-[color:var(--ink-base)]">
                        {c.body}
                      </p>
                      <p className="mt-2 text-[11px] text-[color:var(--ink-soft)]">
                        {c.author?.name ?? 'Sistem'} · {formatDateTime(c.createdAt)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {canDecide && (
                <div className="mt-4 space-y-2 border-t border-[color:var(--surface-line)] pt-4">
                  <Textarea
                    label="Comentariu"
                    value={specComment}
                    onChange={(e) => setSpecComment(e.target.value)}
                    placeholder="Ex: adaugă și export în CSV, limitează la ultimele 30 de zile…"
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => {
                        const body = specComment.trim();
                        if (!body) return;
                        addComment.mutate(
                          { id: request.id, body },
                          {
                            onSuccess: () => setSpecComment(''),
                            onError: (err) =>
                              setTransitionError(err instanceof Error ? err.message : 'Comentariul nu a putut fi salvat.'),
                          },
                        );
                      }}
                      disabled={addComment.isPending || !specComment.trim()}
                    >
                      <Pencil size={15} /> Adaugă comentariu
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transcript + history */}
        <div className="space-y-5 lg:col-span-2">
          <div className="app-shell-panel overflow-hidden rounded-[1.5rem]">
            <div className="border-b border-[color:var(--surface-line)] px-6 py-4">
              <h2 className="text-sm font-semibold text-[color:var(--ink-strong)]">
                Conversația cu AI
              </h2>
              <p className="mt-0.5 text-xs text-[color:var(--ink-muted)]">
                {request._count.messages} mesaje
              </p>
            </div>
            <div className="max-h-[420px] space-y-4 overflow-y-auto px-5 py-4">
              {(request.messages ?? []).length === 0 && (
                <p className="py-6 text-center text-xs text-[color:var(--ink-muted)]">
                  Nicio conversație încă.
                </p>
              )}
              {(request.messages ?? []).map((m) => (
                <div key={m.id} className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <span
                    className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                      m.role === 'user' ? 'bg-primary/12 text-primary' : 'bg-accent/14 text-accent-dark'
                    }`}
                  >
                    {m.role === 'user' ? <UserIcon size={13} /> : <Bot size={13} />}
                  </span>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'rounded-tr-sm bg-primary text-white'
                        : 'rounded-tl-sm bg-[var(--surface-panel-strong)] text-[color:var(--ink-base)]'
                    }`}
                  >
                    <p className={`whitespace-pre-wrap ${m.role === 'user' ? 'text-white' : ''}`}>{m.body}</p>
                    <p
                      className={`mt-1.5 text-[10px] ${
                        m.role === 'user' ? 'text-white/60' : 'text-[color:var(--ink-soft)]'
                      }`}
                    >
                      {timeAgo(m.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status history */}
          {(request.statusHistory ?? []).length > 0 && (
            <div className="app-shell-panel overflow-hidden rounded-[1.5rem]">
              <div className="border-b border-[color:var(--surface-line)] px-6 py-4">
                <h2 className="text-sm font-semibold text-[color:var(--ink-strong)]">Istoric status</h2>
              </div>
              <div className="divide-y divide-[color:var(--surface-line)]">
                {[...(request.statusHistory ?? [])].reverse().map((h) => (
                  <div key={h.id} className="flex items-center gap-3 px-6 py-3">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${FEATURE_REQUEST_STATUS_DOT[h.toStatus]}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[color:var(--ink-base)]">
                        {h.fromStatus ? (
                          <>
                            <span className="text-[color:var(--ink-muted)]">
                              {FEATURE_REQUEST_STATUS_LABELS[h.fromStatus]}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium text-[color:var(--ink-strong)]">
                          {FEATURE_REQUEST_STATUS_LABELS[h.toStatus]}
                        </span>
                      </p>
                      <p className="text-[11px] text-[color:var(--ink-soft)]">
                        {h.changedBy?.name ?? 'Sistem'} · {formatDateTime(h.changedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} title="Aprobă cererea">
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--ink-muted)]">
            Poți adăuga opțional sugestii suplimentare pentru specification sheet — vor fi salvate
            ca comentarii pe specificare înainte de aprobare.
          </p>
          <Textarea
            label="Sugestii pentru specification sheet (opțional)"
            value={approveNotes}
            onChange={(e) => setApproveNotes(e.target.value)}
            placeholder="Ex: adaugă și export în CSV, limitează la ultimele 30 de zile…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              Anulează
            </Button>
            <Button
              disabled={transition.isPending || addComment.isPending}
              onClick={async () => {
                setTransitionError(null);
                const notes = approveNotes.trim();
                try {
                  if (notes) {
                    await addComment.mutateAsync({ id: request.id, body: notes });
                  }
                  transition.mutate(
                    { id: request.id, action: 'approve' },
                    {
                      onSuccess: () => {
                        setApproveOpen(false);
                        setApproveNotes('');
                      },
                      onError: (err) =>
                        setTransitionError(err instanceof Error ? err.message : 'Operațiunea a eșuat.'),
                    },
                  );
                } catch (err) {
                  setTransitionError(
                    err instanceof Error ? err.message : 'Comentariul nu a putut fi salvat.',
                  );
                }
              }}
            >
              <Check size={16} /> Aprobă
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} title="Respinge cererea">
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--ink-muted)]">
            Adaugă un motiv pentru respingere (vizibil în istoric).
          </p>
          <Textarea
            label="Motiv"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex: nu se aliniază cu direcția produsului, efort disproporționat…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Anulează
            </Button>
            <Button
              variant="danger"
              disabled={transition.isPending}
              onClick={() => {
                setTransitionError(null);
                transition.mutate(
                  {
                    id: request.id,
                    action: 'reject',
                    ...(rejectReason.trim() ? { reason: rejectReason.trim() } : {}),
                  },
                  {
                    onSuccess: () => setRejectOpen(false),
                    onError: (err) =>
                      setTransitionError(err instanceof Error ? err.message : 'Operațiunea a eșuat.'),
                  },
                );
              }}
            >
              <X size={16} /> Respinge
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Șterge cererea">
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--ink-muted)]">
            Ești sigur că vrei să ștergi cererea <strong className="font-medium text-[color:var(--ink-strong)]">„{request.title}”</strong>?
            Conversația cu AI-ul și istoricul de status vor fi șterse definitiv. Această acțiune nu
            poate fi anulată.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Anulează
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending}
              onClick={() => {
                del.mutate(request.id, {
                  onSuccess: () => navigate('/admin/feature-requests'),
                });
              }}
            >
              <Trash2 size={16} /> Șterge definitiv
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
